import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Academic Paper Audio';
const heroDescription =
  'Turn research papers into natural-sounding audio with section navigation, citation skipping, and read-along text.';

const featureTitle = 'Listen to the paper itself';
const featureSubtitle = 'Academic document audio';
const featureDescription =
  'Listening creates a playable audio version of the source paper. It does not generate a scripted podcast, summary, or discussion.';
const featureBadge = 'Research papers';
const featureVariant = 'default';
const featureCtaText = 'Listen to a Paper';

const featureFeatures = [
  {
    title: 'Convert research papers into audio',
    description:
      'Listen to the source text with natural-sounding AI narration.',
  },
  {
    title: 'Keep academic structure',
    description:
      'Navigate detected sections such as the abstract, methods, results, and discussion.',
  },
  {
    title: 'Skip citations and references',
    description:
      'Keep citations, references, and footnotes from interrupting the spoken flow.',
  },
  {
    title: 'Read along while listening',
    description: 'Follow the original text as the audio plays.',
  },
];

const comparisonTableData = {
  title: 'Academic Paper Audio Options',
  description:
    'Compare Listening with podcast production tools and basic text-to-speech.',
  columns: [
    {
      header: 'Academic Paper Audio',
      subheader: 'Listening',
      isHighlighted: true,
    },
    { header: 'Anchor', isHighlighted: false },
    { header: 'Riverside', isHighlighted: false },
    { header: 'Generic TTS', isHighlighted: false },
  ],
  rows: [
    {
      feature: 'Academic Content',
      description:
        'Optimization and support for academic research papers and scholarly content.',
      values: [
        { text: 'Designed for research papers', icon: 'check' as const },
        { text: 'Interview/casual focus', icon: 'close' as const },
        { text: 'Live recording focus', icon: 'close' as const },
        { text: 'Basic text reading', icon: 'close' as const },
      ],
    },
    {
      feature: 'Narration',
      description: 'How the source paper becomes audio.',
      values: [
        { text: 'Natural AI voices', icon: 'check' as const },
        { text: 'No specialized training', icon: 'close' as const },
        { text: 'Host must pronounce', icon: 'close' as const },
        { text: 'Often mispronounces', icon: 'close' as const },
      ],
    },
    {
      feature: 'Paper Structure',
      description:
        'Recognition and handling of academic paper organization and formatting.',
      values: [
        { text: 'Detected paper sections', icon: 'check' as const },
        { text: 'Manual editing required', icon: 'close' as const },
        { text: 'Live format only', icon: 'close' as const },
        { text: 'Linear reading only', icon: 'close' as const },
      ],
    },
    {
      feature: 'Citation Handling',
      description:
        'Smart handling of citations, references, and academic formatting elements.',
      values: [
        { text: 'Filters disruptive citations', icon: 'check' as const },
        { text: 'Would read all citations', icon: 'close' as const },
        { text: 'Host must edit out', icon: 'close' as const },
        { text: 'Reads everything', icon: 'close' as const },
      ],
    },
    {
      feature: 'Output',
      description: 'What the tool creates from the paper.',
      values: [
        { text: 'Source-document audio', icon: 'check' as const },
        { text: 'General platforms only', icon: 'warning' as const },
        { text: 'General platforms only', icon: 'warning' as const },
        { text: 'No distribution', icon: 'close' as const },
      ],
    },
  ],
  showIcons: true,
  ariaLabel: 'Academic Paper Audio Comparison Table',
};

const seoSections = [
  {
    title: 'Hear the Research Paper in Its Original Form',
    content:
      'Listening turns the paper’s source text into audio without rewriting it as a podcast episode. The author’s wording, methods, qualifications, and conclusions remain available in the original sequence.',
    contentSecondary:
      'Detected sections make long papers easier to navigate, while citation and reference skipping can keep the main argument moving. Use read-along text whenever a passage needs closer review.',
  },
  {
    title: 'An Audio Reader, Not a Podcast Generator',
    content:
      'Listening is useful when you want to consume an academic document away from the screen or review it by ear. It does not identify key findings, write transitions, create a host script, or synthesize the paper into a new narrative.',
    contentSecondary:
      'If you need a produced podcast episode, you will still need scripting, recording, and editing tools. Listening provides the paper audio for reading and review.',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/academic-podcast-creator',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title: 'Academic Paper Audio - Listen to Research Papers | Listening',
        description:
          'Turn research papers into natural audio with section navigation, citation skipping, adjustable speed, and read-along text.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
    />
  ),
});
